import { fill } from '../addOnStrings.js';
import type { BatchColumn } from './batchColumns';
import { MAX_BATCH_ROWS } from './batchPlan';
import type { BatchPlan, RowError, RowWarning } from './batchPlan';
import { MAX_SPREADSHEET_BYTES, SpreadsheetError } from './spreadsheet';
import type { SpreadsheetErrorCode } from './spreadsheet';

type Strings = Record<string, unknown>;

// The editor's own labels, so a column reads the way the operator knows the field.
const COLUMN_LABELS: Record<BatchColumn, string> = {
    title: 'titleLabel',
    page: 'batchColumnPage',
    content: 'content',
    pretitle: 'pretitle',
    author: 'author',
    byLine: 'byLine',
    imageUrl: 'imageUrl',
    imageCaption: 'imageCaption',
    type: 'batchColumnType',
    insertAs: 'batchColumnInsertAs',
};

const REASONS: Record<RowError['code'], string> = {
    titleMissing: 'batchReasonTitleMissing',
    pageMissing: 'batchReasonPageMissing',
    pageInvalid: 'batchReasonPageInvalid',
    pageOutOfRange: 'batchReasonPageOutOfRange',
    pageUnplaceable: 'batchReasonPageUnplaceable',
    tooLong: 'batchReasonTooLong',
    contentEmptied: 'batchReasonContentEmptied',
    duplicateExisting: 'batchReasonDuplicateExisting',
    duplicateRow: 'batchReasonDuplicateRow',
};

const WARNINGS: Record<RowWarning['code'], string> = {
    embedFellBackToLink: 'batchWarnEmbedFellBackToLink',
    imageUrlInvalid: 'batchWarnImageUrlInvalid',
    imageUnreachable: 'batchWarnImageUnreachable',
    insertAsUnknown: 'batchWarnInsertAsUnknown',
    truncated: 'batchWarnTruncated',
    typeUnknown: 'batchWarnTypeUnknown',
};

const FILE_ERRORS: Record<SpreadsheetErrorCode, string> = {
    unsupportedFile: 'batchErrorUnsupportedFile',
    tooLarge: 'batchErrorTooLarge',
    unreadable: 'batchErrorUnreadable',
    xlsxUnsupported: 'batchErrorXlsxUnsupported',
};

/** The editor's name for the column, or its key in a language that has none. */
export function columnLabel(column: BatchColumn, strings: Strings): string {
    return String(strings[COLUMN_LABELS[column]] ?? column);
}

/** Why a row is skipped, in the operator's language. */
export function errorText(error: RowError, strings: Strings): string {
    const template = strings[REASONS[error.code]];

    switch (error.code) {
        case 'pageOutOfRange':
            return fill(template, { total: error.total });
        case 'tooLong':
            return fill(template, { field: columnLabel(error.field, strings), max: error.max });
        case 'duplicateRow':
            return fill(template, { line: error.line });
        default:
            return fill(template);
    }
}

/** What changes in a row that still goes in. */
export function warningText(warning: RowWarning, strings: Strings): string {
    const template = strings[WARNINGS[warning.code]];
    if (warning.code === 'truncated')
        return fill(template, { field: columnLabel(warning.field, strings), max: warning.max });

    return fill(template);
}

/** A failure that is not the reader's own is a file it could not make sense of. */
export function fileErrorText(error: unknown, strings: Strings): string {
    const code = error instanceof SpreadsheetError ? error.code : 'unreadable';

    return fill(strings[FILE_ERRORS[code]], { max: MAX_SPREADSHEET_BYTES / (1024 * 1024) });
}

/** Why nothing in the sheet can be imported. */
export function fatalText(plan: BatchPlan, strings: Strings): string {
    switch (plan.fatal) {
        case 'missingColumns':
            return fill(strings.batchErrorMissingColumns, {
                columns: plan.mapping.missing.map(column => columnLabel(column, strings)).join(', '),
            });
        case 'tooManyRows':
            return fill(strings.batchErrorTooManyRows, { max: MAX_BATCH_ROWS });
        default:
            return fill(strings.batchErrorEmpty);
    }
}
